// Cart.jsx
// Shopping cart page: lists cart items, shows subtotal, shipping, tax, and total.

import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useState } from "react";
import CartItem from "../components/CartItem";
import {
  formatCurrency,
  calculateShipping,
  calculateTax,
  calculateTotal,
  shippingLabel,
} from "../utils/pricing";

export default function Cart() {
  const { cartItems, subtotal, clearCart } = useCart();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [showClearModal, setShowClearModal] = useState(false);

  const shipping = calculateShipping(subtotal);
  const tax = calculateTax(subtotal);
  const total = calculateTotal(subtotal);

  // ── Empty cart ─────────────────────────────────────────────────────────────
  if (cartItems.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <div className="text-7xl mb-6">🛒</div>
        <h2 className="text-2xl font-bold text-gray-900 mb-3">Your cart is empty</h2>
        <p className="text-gray-500 mb-8">
          Looks like you haven't added anything yet. Go explore!
        </p>
        <Link
          to="/products"
          className="inline-block bg-red-600 text-white px-8 py-3 rounded-xl font-semibold hover:bg-red-700 transition-colors"
        >
          Shop Now
        </Link>
      </div>
    );
  }

  function handleCheckout() {
    if (!currentUser) {
      // Redirect to login, then come back to checkout
      navigate("/login", { state: { from: { pathname: "/checkout" } } });
    } else {
      navigate("/checkout");
    }
  }

  function confirmClearCart() {
    clearCart();
    setShowClearModal(false);
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Shopping Cart</h1>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Cart items list */}
        <div className="flex-1">
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <div className="flex justify-between items-center mb-4">
              <span className="font-semibold text-gray-700">
                {cartItems.length} item{cartItems.length !== 1 ? "s" : ""}
              </span>
              <button
                onClick={() => setShowClearModal(true)}
                className="text-sm text-gray-400 hover:text-red-500 transition-colors"
              >
                Clear all
              </button>
            </div>

            {cartItems.map((item) => (
              <CartItem key={item.id} item={item} />
            ))}
          </div>

          <Link to="/" className="inline-block mt-4 text-sm text-red-600 hover:underline">
            ← Continue Shopping
          </Link>
        </div>

        {/* Order summary */}
        <div className="w-full lg:w-80">
          <div className="bg-white rounded-2xl shadow-sm p-6 sticky top-24">
            <h3 className="text-lg font-bold text-gray-900 mb-5">Order Summary</h3>

            <div className="space-y-3 text-sm">
              <div className="flex justify-between text-gray-600">
                <span>Subtotal</span>
                <span>{formatCurrency(subtotal)}</span>
              </div>

              <div className="flex justify-between text-gray-600">
                <span>Shipping</span>
                <span className={shipping === 0 ? "text-green-600 font-medium" : ""}>
                  {shippingLabel(subtotal)}
                </span>
              </div>

              {subtotal < 100 && (
                <p className="text-xs text-gray-400">
                  Add {formatCurrency(100 - subtotal)} more for free shipping!
                </p>
              )}

              <div className="flex justify-between text-gray-600">
                <span>Tax (8%)</span>
                <span>{formatCurrency(tax)}</span>
              </div>

              <div className="border-t border-gray-100 pt-3 flex justify-between font-bold text-lg text-gray-900">
                <span>Total</span>
                <span className="text-red-600">{formatCurrency(total)}</span>
              </div>
            </div>

            <button
              onClick={handleCheckout}
              className="w-full mt-6 bg-red-600 text-white py-3 rounded-xl font-semibold hover:bg-red-700 active:scale-95 transition-all"
            >
              Proceed to Checkout →
            </button>

            {!currentUser && (
              <p className="text-xs text-gray-400 text-center mt-3">
                You'll be asked to log in before checkout.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Clear Cart Confirmation Modal */}
      <AnimatePresence>
        {showClearModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 overflow-hidden"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-2">Clear Cart?</h3>
              <p className="text-gray-600 text-sm mb-6">
                Are you sure you want to remove all items from your cart?
              </p>
              <div className="flex justify-end gap-3 font-semibold text-sm">
                <button
                  onClick={() => setShowClearModal(false)}
                  className="px-5 py-2.5 rounded-xl text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmClearCart}
                  className="px-5 py-2.5 rounded-xl text-white bg-rose-600 hover:bg-rose-700 transition-colors shadow-sm"
                >
                  Clear All
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
